/**
 * Right-column "Photos & documents" panel of the admissions page.
 * Disabled until the parent draft is saved (`activeStudent` non-null).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CloudArrowUp, ShieldCheck } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { PhotoSlot, DocSlot } from "./UploadSlots";

export function DocumentsPanel({ activeStudent, uploading, onUploadMedical, onUploadPhoto, onUploadDoc }) {
  return (
    <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="vs-card p-6 self-start space-y-5">
      <div>
        <div className="eyebrow">Step 2 — Photos & documents</div>
        <div className="font-display text-base font-semibold mt-1">Photos & documents</div>
        <p className="text-xs text-[var(--vs-text-secondary)] mt-1">Available once Step 1 is saved.</p>
      </div>

      {!activeStudent ? (
        <div className="p-4 rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] text-xs text-[var(--vs-text-secondary)]">
          Save the draft first to enable photo & document uploads.
        </div>
      ) : (
        <>
          <div className="p-3 rounded-md border border-[var(--vs-secondary)]/30 bg-[var(--vs-secondary)]/5 text-[11px] text-[var(--vs-text-secondary)] leading-relaxed" data-testid="photo-instructions">
            <strong className="text-[var(--vs-text)]">Photo guidelines:</strong> Passport-style, front-facing, plain background, ≤ 2MB, JPG/PNG/WEBP. Student photo is mandatory for ID cards; parent & guardian photos are recommended for security verification.
          </div>
          <PhotoSlot label="Student photo" testId="upload-student-photo" path={activeStudent.photo_path} onSelect={(f) => onUploadPhoto(activeStudent.id, "student", f)} />
          <PhotoSlot label="Parent photo" testId="upload-parent-photo" path={activeStudent.parent_photo_path} onSelect={(f) => onUploadPhoto(activeStudent.id, "parent", f)} />
          <PhotoSlot label="Guardian photo" testId="upload-guardian-photo" path={activeStudent.guardian_photo_path} onSelect={(f) => onUploadPhoto(activeStudent.id, "guardian", f)} />

          <DocSlot label="Birth certificate" testId="upload-birth-cert" onSelect={(f) => onUploadDoc(activeStudent.id, "birth_cert", f)} />
          <DocSlot label="Address proof" testId="upload-address-proof" onSelect={(f) => onUploadDoc(activeStudent.id, "address_proof", f)} />

          <div className="vs-divider" />
          <div>
            <div className="font-display text-sm font-semibold flex items-center gap-2">
              <ShieldCheck size={14} className="text-[var(--vs-secondary)]" /> Medical certificate (mandatory gate)
            </div>
            <p className="text-xs text-[var(--vs-text-secondary)] mt-1">
              Status pinned to <Badge variant="outline" className="ml-1 border-[var(--vs-warning)]/40 text-[var(--vs-warning)]">DRAFT</Badge> until uploaded.
            </p>
            <label className="mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-[var(--vs-border-focus)]/40 hover:border-[var(--vs-secondary)] cursor-pointer transition-colors">
              <CloudArrowUp size={18} weight="duotone" />
              <span className="text-sm">{uploading ? "Uploading…" : "Choose PDF / JPG (max 5MB)"}</span>
              <input data-testid="medical-cert-input" type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => onUploadMedical(activeStudent.id, e.target.files?.[0])} />
            </label>
          </div>

          <div className="text-[11px] text-[var(--vs-text-secondary)]">
            Need to manage all docs?{" "}
            <Link to={`/app/students/${activeStudent.id}`} className="underline">Open full manage page →</Link>
          </div>
        </>
      )}
    </motion.div>
  );
}
